import classNames from 'classnames/bind';
import { useField } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'uikit/Button';
import InputText from 'uikit/InputText';
import InputTimer from 'uikit/InputTimer';

import VideoPreview from '../VideoPreview';
import styles from './VideoLink.module.scss';

const cx = classNames.bind(styles);

const VideoLink = ({ 'data-testid': dataTestId, name }) => {
  const { t } = useTranslation('', {
    keyPrefix: 'uikit/VideoPicker/VideoLink',
  });

  const [, , { setValue: setVideo }] = useField(name);
  const [{ value: videoLink }, , { setValue: setVideoLink }] =
    useField('videoLink');
  const [{ value: startTime }, , { setValue: setStartTime }] =
    useField('startTime');
  const [{ value: endTime }, , { setValue: setEndTime }] = useField('endTime');
  useField('videoLink');

  const formattedVideoLink =
    videoLink
      ?.replace('https://youtu.be/', 'https://www.youtube.com/embed/')
      .replace(
        'https://www.youtube.com/watch?v=',
        'https://www.youtube.com/embed/',
      )
      // convert the param "t" to "start" in the url
      .replace(/&t=(\d+?)[s]/, '?start=$1') || '';

  useEffect(() => {
    if (formattedVideoLink) {
      const startTimeRegex = /start=(\d+)&?/g;
      const endTimeRegex = /end=(\d+)&?/g;
      const linkValue = formattedVideoLink
        .replace(startTimeRegex, '')
        .replace(endTimeRegex, '')
        .trim();

      const linkWithTimer = `${linkValue}${
        !linkValue.includes('?') && (startTime || endTime) ? '?' : ''
      }${startTime ? `start=${startTime}` : ''}${
        startTime && endTime && endTime > startTime ? '&' : ''
      }${
        endTime && (!startTime || endTime > startTime) ? `end=${endTime}` : ''
      }`;

      setVideo(linkWithTimer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTime, formattedVideoLink, startTime]);

  function handleLinkChange(event) {
    if (event.target.value === '') {
      handleLinkReset();
    } else {
      setVideoLink(event.target.value);
    }
  }

  function handleLinkReset() {
    setVideo('');
    setVideoLink('');
    setEndTime(null);
    setStartTime(null);
  }

  const isYoutube = formattedVideoLink?.startsWith(
    'https://www.youtube.com/embed/',
  );

  return (
    <>
      <div className={cx('link-input-container')}>
        <InputText
          aria-label={t('aria-link')}
          className={cx('link')}
          data-testid={dataTestId}
          name="videoLink"
          onChange={handleLinkChange}
          placeholder={t('link-placeholder')}
        />
        {videoLink && (
          <Button
            aria-label={t('delete')}
            className={cx('link-reset-button')}
            icon="times"
            onClick={handleLinkReset}
          />
        )}
      </div>
      {formattedVideoLink && <VideoPreview video={formattedVideoLink} />}
      {formattedVideoLink && isYoutube && (
        <div className={cx('timer')}>
          <InputTimer
            className={cx('input-timer')}
            label={t('start-time-label')}
            name="startTime"
            setTotalSeconds={setStartTime}
            totalSeconds={startTime}
          />
          <InputTimer
            className={cx('input-timer')}
            label={t('end-time-label')}
            name="endTime"
            setTotalSeconds={setEndTime}
            totalSeconds={endTime}
          />
        </div>
      )}
    </>
  );
};

VideoLink.propTypes = {
  'data-testid': PropTypes.string,
  name: PropTypes.string.isRequired,
};

export default VideoLink;
